import React, { useState, useEffect } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Add, EventNote } from "@mui/icons-material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { getToken } from "services/auth.service";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePageTitle } from "context/PageTitleContext";
import AddEventDialog from "../../components/calendars/AddEventDialog";
import EventDetailPopover from "../../components/calendars/EventDetailPopover";
import AddEventFromContractDialog from "../../components/calendars/AddEventFromContractDialog";
import "./CalendarPage.css";
import { AppButton } from "components/common/buttons";

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [addFromContractDialogOpen, setAddFromContractDialogOpen] =
    useState(false);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { setPageTitle } = usePageTitle();

  // Set page title
  useEffect(() => {
    setPageTitle("Lịch biểu");
  }, [setPageTitle]);

  // Fetch events từ API
  const fetchEvents = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Transform data cho FullCalendar format
        const calendarEvents = data.map((event) => ({
          id: event.eventId.toString(),
          title: event.title,
          start: event.startDate,
          end: event.endDate,
          allDay: event.isAllDay,
          backgroundColor: event.color,
          borderColor: event.color,
          extendedProps: {
            description: event.description,
            contractId: event.contractId,
            contractNumber: event.contractNumber,
            contractName: event.contractName,
            itemId: event.itemId,
            itemCode: event.itemCode,
            itemName: event.itemName,
            createdBy: event.createdBy,
            createdByName: event.createdByName,
            notes: event.notes,
            eventType: event.eventType,
            canEdit: event.canEdit,
          },
        }));
        setEvents(calendarEvents);
      } else {
        throw new Error("Không thể tải danh sách sự kiện");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      showSnackbar("Lỗi khi tải danh sách sự kiện", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle permission check and show error message
  const showPermissionError = (message) => {
    showSnackbar(message, "warning");
  };

  // Handle "Thêm từ hợp đồng" with permission check
  const handleAddFromContract = async () => {
    const result = await checkPermission(PERMISSIONS.CONTRACT.CALENDAR_CREATE);
    if (result.hasPermission) {
      setAddFromContractDialogOpen(true);
    } else {
      showPermissionError("Bạn không có quyền tạo sự kiện từ hợp đồng.");
    }
  };

  // Handle "Tạo sự kiện" with permission check
  const handleCreateEvent = async () => {
    const result = await checkPermission(PERMISSIONS.CONTRACT.CALENDAR_CREATE);
    if (result.hasPermission) {
      setSelectedDate(null);
      setEditingEvent(null); // Reset editing event for create mode
      setAddEventDialogOpen(true);
    } else {
      showPermissionError("Bạn không có quyền tạo sự kiện.");
    }
  };

  // Xử lý khi click vào ngày trên calendar với kiểm tra quyền
  const handleDateClick = async (selectInfo) => {
    const result = await checkPermission(PERMISSIONS.CONTRACT.CALENDAR_CREATE);
    if (result.hasPermission) {
      setSelectedDate(selectInfo.dateStr);
      setEditingEvent(null); // Reset editing event for create mode
      setAddEventDialogOpen(true);
    } else {
      showPermissionError("Bạn không có quyền tạo sự kiện.");
    }
  };

  // Xử lý khi click vào event
  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    setSelectedEvent(event);
    setEventDetailOpen(true);
  };

  // Xử lý khi tạo event thành công
  const handleEventCreated = () => {
    fetchEvents(); // Refresh danh sách events
    setAddEventDialogOpen(false);
    setAddFromContractDialogOpen(false);
    showSnackbar("Tạo sự kiện thành công");
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <AppButton
            variant="outlined"
            startIcon={<EventNote />}
            onClick={handleAddFromContract}
          >
            Thêm từ hợp đồng
          </AppButton>

          <AppButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateEvent}
          >
            Tạo sự kiện
          </AppButton>
        </Box>
      </Box>

      {/* Calendar */}
      <Paper
        sx={{
          p: 3,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <FullCalendar
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              listPlugin,
              interactionPlugin,
            ]}
            initialView="dayGridMonth"
            locale="vi"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
            }}
            buttonText={{
              today: "Hôm nay",
              month: "Tháng",
              week: "Tuần",
              day: "Ngày",
              list: "Danh sách",
            }}
            events={events}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            height="auto"
            dayMaxEvents={3}
            moreLinkText="thêm"
            eventDisplay="block"
            dayHeaderFormat={{ weekday: "short" }}
            titleFormat={{ year: "numeric", month: "long" }}
            firstDay={1} // Bắt đầu tuần từ thứ 2
            weekNumberCalculation="ISO"
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
            slotLabelFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
          />
        )}
      </Paper>

      {/* Add Event Dialog */}
      <AddEventDialog
        open={addEventDialogOpen}
        onClose={() => {
          setAddEventDialogOpen(false);
          setEditingEvent(null);
        }}
        onEventCreated={handleEventCreated}
        selectedDate={selectedDate}
        editingEvent={editingEvent}
      />

      {/* Add Event From Contract Dialog */}
      <AddEventFromContractDialog
        open={addFromContractDialogOpen}
        onClose={() => setAddFromContractDialogOpen(false)}
        onEventsCreated={handleEventCreated}
      />

      {/* Event Detail Dialog */}
      <EventDetailPopover
        open={eventDetailOpen}
        onClose={() => {
          setEventDetailOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onEdit={(event) => {
          setEditingEvent(event);
          setAddEventDialogOpen(true);
          setEventDetailOpen(false);
        }}
        onDelete={(eventId) => {
          setEvents((prev) => prev.filter((e) => e.id !== eventId));
          showSnackbar("Xóa sự kiện thành công", "success");
        }}
      />

      {/* Add Event From Contract Dialog */}
      <AddEventFromContractDialog
        open={addFromContractDialogOpen}
        onClose={() => setAddFromContractDialogOpen(false)}
        onEventCreated={(result) => {
          fetchEvents(); // Refresh events
          showSnackbar(
            `Tạo thành công ${result.eventsCreated || 1} sự kiện từ hợp đồng`,
            "success"
          );
        }}
      />

      {/* Snackbar */}
      <AppSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};

export default CalendarPage;
